/*
 * Copyright (c) 2023 CalAmp Corp. All Rights Reserved
 */
import React, {
  forwardRef,
  memo,
  useImperativeHandle
} from 'react';
import isEmpty from 'lodash.isempty';
import {translate} from '@ion/core/src/locale/locale';
import {Link} from '@ion/core/src/types/entities';
import styles from '@ion/dls/src/editors/editorStyles';
import EmbeddedForm from '@ion/dls/src/editors/forms/EmbeddedForm';
import {
  useExtendStyles
} from '@ion/dls/src/hooks/useExtendStyles';
import {
  useFormSave
} from '@ion/dls/src/hooks/useFormSave';
import {
  FormState
} from '@ion/dls/src/hooks/useFormState';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {DLSTheme} from '@ion/dls/src/types/types';
import {
  useCurrentGlobalGroup
} from '../hooks/useCurrentGlobalGroup';
import {
  useSanitizeAccount
} from '../hooks/useSanitizeAccount';
import {useVBusStatus, VBusStatusProps} from './useVBusStatus';
import VBusStatus from './VBusStatus';

const useStyles = (theme: DLSTheme) => ({
  errorMessage : {
    color      : dlsColor('caRed'),
    fontSize   : '12px',
    fontFamily : 'Roboto',
    fontWeight : 400,
    marginLeft : '15px'
  }
});

interface VBusStatusDetailsFormProps extends VBusStatusProps {
  onSave: (details: VBusStatusDetails) => void;
}

type VBusStatusDetails = {
  job: {
    account: Link;
    name: string;
    description: string;
    tasks: [{
      task: {
        description: string;
        name: string;
        taskType: string;
        metaData: {
          commandType: string;
          obdControl: {
            action: VBusStatusAction;
          }
        },
        account: Link;
      }
    }]
  }
}

export enum VBusStatusAction {
  ON = 'ON',
  OFF = 'OFF'
}

export enum VBusDetailsFields {
  ACCOUNT = 'account',
  ACTION = 'action',
  NAME = 'name',
  TASK_NAME = 'taskName'
}

enum VBusDetailsValues {
  COMMAND_TYPE = 'ObdControl',
  TASK_TYPE = 'Command'
}

/**
 * Re-usable form with the details for VBUS Status entity.
*/
export const VBusStatusDetailsForm = forwardRef(({
  setTaskNickNameAction,
  onSave
}: VBusStatusDetailsFormProps, ref) => {
  const editorStyles = useExtendStyles(styles, useStyles);
  const classes      = editorStyles();
  const path         = 'common:editors.bulkActions.vBus';
  const globalGroup  = useCurrentGlobalGroup();
  const account      = useSanitizeAccount(globalGroup);
  const {
    setFormState,
    formState,
    actionEntry,
    updateAction,
    getTaskName,
    getName,
    setShowSnackBar,
    showSnackBar
  } = useVBusStatus({setTaskNickNameAction});

  const serializer = (formState: FormState): VBusStatusDetails => {
    const {
      ACTION
    } = VBusDetailsFields;
    const {
      COMMAND_TYPE,
      TASK_TYPE
    } = VBusDetailsValues;

    return {
      job : {
        account     : account,
        name        : getName(actionEntry?.action?.action),
        description : null,
        tasks       : [{
          task : {
            description : '',
            name        : getTaskName(actionEntry?.action?.action),
            taskType    : TASK_TYPE,
            metaData    : {
              commandType : COMMAND_TYPE,
              obdControl  : {
                action : formState[ACTION]?.value
              }
            },
            account : account
          }
        }]
      }
    };
  };

  const handleSubmit = (details: VBusStatusDetails) => {
    if (isEmpty(formState)) {
      setShowSnackBar(true);
    } else {
      onSave({
        ...details
      });
    }
  };

  const onSubmit = useFormSave({
    onSave    : handleSubmit,
    serialize : serializer,
    formState,
    setFormState
  });

  useImperativeHandle(ref, () => ({
    onSubmit
  }), [onSubmit]);

  return (
    <EmbeddedForm>
      <VBusStatus id={1}
        updateAction={updateAction}
        action={actionEntry?.action}
        path={path} />
      {showSnackBar && (
        <span className={classes.errorMessage}>{translate('common:editors.shared.required')}</span>
      )}
    </EmbeddedForm>
  );
});

export default memo(VBusStatusDetailsForm);
